import { Button, EmptyState, Icon, Panel } from '@dayon/design-system';
import { formatDeclaredNameForStaff } from '@dayon/contracts';
import { formatPesos } from '@dayon/money';
import { CorrectedAfterRefusal } from '../../identity/CorrectedAfterRefusal.tsx';
import { PersonName } from '../../identity/PersonName.tsx';
import { CLOCK, formatCalendarDay, formatDay } from '../format.ts';
import type { NoteRow, PlayerRecord } from '../types.ts';

export function ActivityTab({
  player,
  notes,
  canNote,
  onAddNote,
  role,
}: {
  player: PlayerRecord;
  notes: NoteRow[];
  canNote: boolean;
  onAddNote: () => void;
  role: string;
}) {
  /* Read under their own names: written as a property of `player`, the field spells the table it
     comes from, and the table-name scan (no-schema-names-on-screen.test.ts) cannot tell the
     two apart. */
  const {
    declared_name: signUpName,
    declared_name_shown: signUpNameShown,
    corrected_details: correctedDetails,
  } = player;
  /* A CORRECTION AFTER A REFUSAL, under the first entry it changed — by the rule the name is shown
     by: only where the route says it sent the name, whatever else arrived beside it. */
  const correction = signUpNameShown === true ? (correctedDetails ?? null) : null;
  return (
    <div className="split">
      <Panel title="Recent game history">
        <EmptyState icon="game-controller" title="Game rounds are not on this page">
          A list of the last rounds with bet, win and time is not shown. Those rounds are recorded,
          and the platform is even set up to look them up quickly by player — but what this profile
          can read is only the player record, their restrictions, their notes and their KYC
          submissions. Nothing is drawn here rather than a sample ledger an
          operator could mistake for play. The money those rounds moved <em>is</em> readable: it is
          on the Transactions tab as Bet and Win rows, and the Lifetime GGR tile above is bets less
          wins over every non-voided round.
        </EmptyState>
      </Panel>

      <div className="stack">
        <Panel title="Balances">
          <dl className="stack" style={{ margin: 0 }}>
            <div className="def-row">
              <dt>Cash</dt>
              <dd>{formatPesos(BigInt(player.cash_balance))}</dd>
            </div>
          </dl>
          {/*
            WHERE THE FIGURE COMES FROM, AND WHY THIS NOTICE NO LONGER SAYS THE
            OTHER BALANCES CANNOT BE READ.

            Cash is `player.cash_balance`, which the profile route computes with
            `wallet.player_cash_balance()` — a sum of that player's PLAYER_CASH
            ledger entries, evaluated per request, with no stored balance column
            anywhere for it to disagree with.

            This notice used to read, in full: "Bonus (locked), Points and
            Withdrawable are missing, not zero. Only wallet.player_cash_balance()
            is exposed by this route; the locked-bonus balance needs the
            PLAYER_BONUS and PLAYER_LOCKED accounts on wallet.account.
            Withdrawable is derived as cash minus locked bonus, so without the
            second term it cannot be shown at all." Three things were wrong with
            it, and only the first is cosmetic.

            It named a function and a table — `wallet.account` — to somebody
            deciding a payout. That identifier is not a thing an operator can
            look up, ask for or repeat to anyone, which is the whole point of
            `no-schema-names-on-screen.test.ts`; it belongs here, in the source,
            where a developer needs to know which data backs which claim.

            It had stopped being TRUE. All three figures are read now and all
            three are on the Balances tab: the locked bonus is the balance of the
            player's PLAYER_BONUS `wallet.account`, summed from the same ledger
            the cash figure is summed from; points come from
            `points.points_ledger` as a count; and "Available to withdraw" is
            `cash − heldInOpenRequests`, the same arithmetic the player's own
            wallet and withdrawal routes apply. PLAYER_LOCKED, which the old
            sentence also named, is a wallet kind nothing on this profile reads
            or needs.

            And its DEFINITION was wrong in the expensive direction. Withdrawable
            is cash less open requests, NOT cash less locked bonus: bonus money
            sits in its own account and was never inside the cash figure to
            subtract, so subtracting it would deduct the same money twice and
            understate every payout. `BalancesTab.tsx` records that finding at
            length, and this notice must not resurrect the arithmetic it
            replaced.

            WHAT IS KEPT is the one thing an operator standing in front of this
            card actually needs from it: cash is not the amount the player may
            take out, so a payout is decided on the tab that computes that, not
            from this number. The old sentence said as much — "it is the one
            figure on this card a payout decision turns on" — and that part was
            right.
          */}
          <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="info" />
            <div>
              Cash is the only balance on this card, and it is not the amount the player may take
              out — <strong>do not settle a payout from this figure</strong>. The bonus money being
              held for this player, their points and the amount actually available to withdraw are
              all read: they are on the <strong>Balances</strong> tab, side by side, each with a
              line saying what it does and does not include, and that tab says so plainly when your
              own access does not stretch to one of them. So none of the three is absent here
              because a read failed, and none of them is zero — this card asks only for cash. The
              points count has a tab of its own as well.
            </div>
          </div>
        </Panel>

        <Panel title="Account record">
          <dl className="stack" style={{ margin: 0 }}>
            {/* THE NAME THE PLAYER GAVE AT SIGN-UP, as they say their ID prints it — the name the
                identity check compared with the ID. A legal name: the route sends it only to a
                reader holding kyc.review, and says whether it did (`declared_name_shown`). Three
                states, three sentences: the name, an account that was never asked, and a reader
                the route withheld it from — never an em-dash, which reads as "not captured".

                DECIDED BY WHAT THE ROUTE SAYS IT SENT, and never by this console's own copy of
                the reader's permissions: that is read at sign-in, while the route reads them on
                every request, so a role that lost the key mid-session was told "No name was asked
                at sign-up" of every player who gave one, and a role that gained it was told it
                did not hold the key. For the same reason the refusal says "your role", not the
                role this console last read: the reader may since have been moved to another.
                The name is drawn only where the route says it sent it; with neither key (an API
                older than this console, for the length of a deploy) the row is not drawn at all
                rather than guessing which of the three is true. And a missing name key is read
                as the null it stands for — `undefined` handed to the formatter throws on its
                `.suffix`, and the whole profile became "This screen could not be shown". (Not
                `== null`: the house's eqeqeq refuses it, so both are named.)

                The refusal names the key and who to ask, as the KYC tab's and Identity checks'
                refusals of the same key do — in the muted text token: the subtle grey measured
                4.44:1 at worst in dark here, under the 4.5:1 floor. */}
            {typeof signUpNameShown === 'boolean' && (
              <div className="def-row">
                <dt>Name given at sign-up</dt>
                <dd style={{ minWidth: 0 }}>
                  {!signUpNameShown ? (
                    <>
                      <span className="text-muted">Not shown</span>
                      <div className="text-muted">
                        Seeing it needs <span className="tabular">kyc.review</span>, which your role
                        does not hold. Ask an administrator if you need it.
                      </div>
                    </>
                  ) : (
                    <>
                      {signUpName === null || signUpName === undefined ? (
                        <span className="text-subtle">No name was asked at sign-up</span>
                      ) : (
                        <PersonName>{formatDeclaredNameForStaff(signUpName)}</PersonName>
                      )}
                      {/* "Yes, keep the first": the name given at sign-up stays, and the one the
                          player corrected it to after a refusal sits under it, with its day. */}
                      {correction?.name ? (
                        <CorrectedAfterRefusal day={formatCalendarDay(correction.corrected_on)}>
                          <PersonName>{formatDeclaredNameForStaff(correction.name)}</PersonName>
                        </CorrectedAfterRefusal>
                      ) : null}
                    </>
                  )}
                </dd>
              </div>
            )}
            <div className="def-row">
              <dt>Brand</dt>
              <dd>{player.brand_name}</dd>
            </div>
            <div className="def-row">
              <dt>Phone</dt>
              <dd>{player.phone ?? '—'}</dd>
            </div>
            <div className="def-row">
              <dt>Email</dt>
              <dd>{player.email ?? '—'}</dd>
            </div>
            <div className="def-row">
              <dt>Date of birth</dt>
              {/* Nullable in the schema and null for most seeded rows. An
                  em-dash says "not captured"; a fabricated date would be read
                  as an age check that had been performed. */}
              <dd>
                {player.date_of_birth ? formatDay(player.date_of_birth) : '—'}
                {/* And the date the player corrected it to after a refusal, under it, by the same
                    rule as the name: a calendar day, never read through the browser's clock. */}
                {correction?.date_of_birth ? (
                  <CorrectedAfterRefusal day={formatCalendarDay(correction.corrected_on)}>
                    {formatCalendarDay(correction.date_of_birth)}
                  </CorrectedAfterRefusal>
                ) : null}
              </dd>
            </div>
            <div className="def-row">
              <dt>Agent</dt>
              <dd>{player.agent_ref ?? '—'}</dd>
            </div>
            <div className="def-row">
              <dt>Registered</dt>
              <dd>{formatDay(player.registered_at)}</dd>
            </div>
            <div className="def-row">
              <dt>Last seen</dt>
              <dd>{player.last_seen_at ? formatDay(player.last_seen_at) : 'never'}</dd>
            </div>
          </dl>
        </Panel>

        <Panel
          title="Internal notes"
          actions={
            canNote ? (
              <Button variant="ghost" small onClick={onAddNote}>
                <Icon name="plus" />
                Add
              </Button>
            ) : null
          }
        >
          <div className="notice notice-quiet" style={{ marginBottom: 'var(--space-3)' }}>
            <Icon name="info" />
            <div>
              Notes are staff-only and can only be added to: once saved, nothing on the platform can
              edit or delete one — not this screen, not a background job, not anyone working behind
              the screens — which is what makes them usable as evidence. Correcting one means writing
              a second note that says so. Saving one also lands it in the tamper-evident audit
              trail at the same moment, so the note and its record cannot come apart.
              {!canNote && (
                <>
                  {' '}
                  Writing one needs <span className="tabular">players.note</span> and you are {role}
                  , so the Add control is not drawn.
                </>
              )}
            </div>
          </div>

          {notes.length === 0 ? (
            <EmptyState icon="note" title="Nothing has been written on this account">
              No note has been written for this player. This is a real zero — the notes come back
               with the record on every read, never from a cache.
            </EmptyState>
          ) : (
            <ul
              className="stack"
              aria-label="Internal notes, newest first"
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
            >
              {notes.map((n) => (
                <li key={n.id}>
                  <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{n.body}</div>
                  <div className="text-subtle" style={{ fontSize: '11.5px' }}>
                    {n.author_label} · {formatDay(n.created_at)} ·{' '}
                    {CLOCK.format(new Date(n.created_at))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
